// Phase 4 — Gouvernance : HSE, EPI, Juridique, Courrier, Workflows
import React, { useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import GenericModule from "@/components/GenericModule";
import { api } from "@/lib/api";

export function HSE() {
  return (
    <GenericModule
      title="HSE — Incidents"
      subtitle="Hygiène · Sécurité · Environnement"
      dataTestId="hse"
      endpoint="/hse"
      newButtonLabel="Signaler un incident"
      extractStats={(d) => [
        { label: "Total incidents", value: d.total || 0, count: true },
        { label: "Critiques", value: d.by_gravity?.critique || 0, count: true, accent: "bad" },
        { label: "Majeurs", value: d.by_gravity?.majeur || 0, count: true, accent: "warn" },
        { label: "Ouverts", value: d.open_count || 0, count: true, accent: "citron", highlight: true },
      ]}
      columns={[
        { key: "ref", label: "Réf", type: "code" },
        { key: "date", label: "Date", type: "date" },
        { key: "type", label: "Type" },
        {
          key: "gravity", label: "Gravité", type: "pill",
          pillClass: (r) => ({ mineur: "bg-ok/15 text-ok", modere: "bg-warn/15 text-warn", majeur: "bg-bad/15 text-bad", critique: "bg-bad text-white" })[r.gravity],
        },
        { key: "location", label: "Lieu" },
        { key: "description", label: "Description" },
        {
          key: "status", label: "Statut", type: "pill",
          pillClass: (r) => ({ ouvert: "bg-bad/15 text-bad", en_traitement: "bg-warn/15 text-warn", ferme: "bg-ok/15 text-ok" })[r.status],
        },
      ]}
      fields={[
        { key: "date", label: "Date", type: "date", required: true, group: 1 },
        { key: "type", label: "Type", type: "select", options: ["Accident", "Quasi-accident", "Risque", "Non-conformité", "Maladie pro", "Environnement"], required: true, group: 1 },
        { key: "gravity", label: "Gravité", type: "select", options: [{ value: "mineur", label: "Mineur" }, { value: "modere", label: "Modéré" }, { value: "majeur", label: "Majeur" }, { value: "critique", label: "Critique" }], required: true, group: 1 },
        { key: "location", label: "Lieu", group: 2 },
        { key: "reported_by", label: "Signalé par", group: 2 },
        { key: "status", label: "Statut", type: "select", options: ["ouvert", "en_traitement", "ferme"], group: 2 },
        { key: "description", label: "Description", type: "textarea", required: true, voice: true },
        { key: "actions_taken", label: "Actions correctives", type: "textarea", voice: true },
      ]}
      defaults={{ date: new Date().toISOString().slice(0, 10), type: "Quasi-accident", gravity: "mineur", location: "", reported_by: "", status: "ouvert", description: "", actions_taken: "" }}
    />
  );
}

export function EPI() {
  return (
    <GenericModule
      title="Dotations EPI"
      subtitle="Équipements de Protection Individuelle"
      dataTestId="epi"
      endpoint="/epi"
      newButtonLabel="Nouvelle dotation"
      extractStats={(d) => [
        { label: "Dotations totales", value: d.total || 0, count: true },
        { label: "À renouveler", value: (d.expiring || []).length, count: true, accent: "warn", highlight: true },
      ]}
      columns={[
        { key: "date", label: "Date", type: "date" },
        { key: "employee_name", label: "Employé" },
        { key: "item", label: "Équipement" },
        { key: "quantity", label: "Qté", type: "number", align: "right" },
        { key: "next_renewal", label: "Renouv.", type: "date" },
      ]}
      fields={[
        { key: "employee_name", label: "Employé", required: true, group: 1 },
        { key: "item", label: "Équipement", required: true, placeholder: "Casque, Chaussures S3, Gants…", group: 1 },
        { key: "date", label: "Date dotation", type: "date", required: true, group: 2 },
        { key: "quantity", label: "Quantité", type: "number", group: 2 },
        { key: "next_renewal", label: "Prochain renouvellement", type: "date", group: 2 },
        { key: "notes", label: "Notes", type: "textarea" },
      ]}
      defaults={{ employee_name: "", item: "", date: new Date().toISOString().slice(0, 10), quantity: 1, next_renewal: "", notes: "" }}
    />
  );
}

export function Legal() {
  return (
    <GenericModule
      title="Juridique — Contrats"
      subtitle="Contrats, baux, assurances"
      dataTestId="legal"
      endpoint="/legal"
      newButtonLabel="Nouveau contrat"
      extractStats={(d) => [
        { label: "Contrats actifs", value: d.active_count || 0, count: true },
        { label: "Expirés / à renouveler", value: (d.expiring || []).length, count: true, accent: "warn", highlight: true },
      ]}
      columns={[
        { key: "type", label: "Type" },
        { key: "title", label: "Intitulé" },
        { key: "party", label: "Cocontractant" },
        { key: "start_date", label: "Début", type: "date" },
        { key: "end_date", label: "Fin", type: "date" },
        { key: "amount", label: "Montant", type: "money", align: "right" },
        {
          key: "status", label: "Statut", type: "pill",
          pillClass: (r) => ({ actif: "bg-ok/15 text-ok", expire: "bg-warn/15 text-warn", resilié: "bg-bad/15 text-bad" })[r.status],
        },
      ]}
      fields={[
        { key: "type", label: "Type", type: "select", options: ["Bail", "Assurance", "Contrat fournisseur", "NDA", "Prestation", "Travail", "Autre"], required: true, group: 1 },
        { key: "title", label: "Intitulé", required: true, group: 1 },
        { key: "party", label: "Cocontractant", required: true, group: 2 },
        { key: "amount", label: "Montant (XAF)", type: "number", group: 2 },
        { key: "start_date", label: "Date début", type: "date", required: true, group: 3 },
        { key: "end_date", label: "Date fin", type: "date", group: 3 },
        { key: "status", label: "Statut", type: "select", options: ["actif", "expire", "resilié"], group: 3 },
        { key: "notes", label: "Notes", type: "textarea" },
      ]}
      defaults={{ type: "Bail", title: "", party: "", amount: 0, start_date: new Date().toISOString().slice(0, 10), end_date: "", status: "actif", notes: "" }}
    />
  );
}

export function Mail() {
  return (
    <GenericModule
      title="Courrier"
      subtitle="Suivi des courriers entrants et sortants"
      dataTestId="mail"
      endpoint="/mail"
      newButtonLabel="Nouveau courrier"
      extractStats={(d) => [
        { label: "Total courriers", value: d.total || 0, count: true },
        { label: "En attente", value: d.pending || 0, count: true, accent: "warn", highlight: true },
      ]}
      columns={[
        { key: "ref", label: "Réf", type: "code" },
        { key: "date", label: "Date", type: "date" },
        {
          key: "direction", label: "Sens", type: "pill",
          pillClass: (r) => r.direction === "entrant" ? "bg-citron/15 text-citron" : "bg-obsidian-800",
        },
        { key: "sender", label: "Expéditeur" },
        { key: "recipient", label: "Destinataire" },
        { key: "subject", label: "Objet" },
        { key: "category", label: "Catégorie" },
        {
          key: "status", label: "Statut", type: "pill",
          pillClass: (r) => ({ a_traiter: "bg-warn/15 text-warn", en_cours: "bg-citron/15 text-citron", traite: "bg-ok/15 text-ok", archive: "bg-obsidian-800" })[r.status],
        },
      ]}
      fields={[
        { key: "direction", label: "Sens", type: "select", options: ["entrant", "sortant"], required: true, group: 1 },
        { key: "date", label: "Date", type: "date", required: true, group: 1 },
        { key: "category", label: "Catégorie", placeholder: "Administratif, Fiscal, RH…", group: 1 },
        { key: "sender", label: "Expéditeur", required: true, group: 2 },
        { key: "recipient", label: "Destinataire", required: true, group: 2 },
        { key: "subject", label: "Objet", required: true },
        { key: "reference", label: "Référence externe" },
        { key: "status", label: "Statut", type: "select", options: ["a_traiter", "en_cours", "traite", "archive"] },
        { key: "notes", label: "Notes", type: "textarea" },
      ]}
      defaults={{ direction: "entrant", date: new Date().toISOString().slice(0, 10), category: "Général", sender: "", recipient: "", subject: "", reference: "", status: "a_traiter", notes: "" }}
    />
  );
}

export function Workflows() {
  async function decide(id, decision) {
    const comment = decision === "reject" ? prompt("Motif du rejet (optionnel)") : "";
    await api.patch(`/workflows/${id}/decide`, { decision, comment: comment || "" });
    window.location.reload();
  }
  return (
    <GenericModule
      title="Workflows de validation"
      subtitle="Demandes nécessitant approbation"
      dataTestId="workflows"
      endpoint="/workflows"
      newButtonLabel="Nouvelle demande"
      extractStats={(d) => [
        { label: "À valider par moi", value: (d.pending_for_me || []).length, count: true, accent: "citron", highlight: true },
        { label: "En attente (total)", value: d.pending_count || 0, count: true, accent: "warn" },
      ]}
      columns={[
        { key: "ref", label: "Réf", type: "code" },
        { key: "type", label: "Type" },
        { key: "title", label: "Demande" },
        { key: "requested_by_email", label: "Demandeur", muted: true },
        { key: "target_role", label: "À approuver par", type: "pill" },
        { key: "amount", label: "Montant", type: "money", align: "right" },
        {
          key: "status", label: "Statut", type: "pill",
          pillClass: (r) => ({ pending: "bg-warn/15 text-warn", approved: "bg-ok/15 text-ok", rejected: "bg-bad/15 text-bad" })[r.status],
        },
      ]}
      rowActions={(row) => row.status === "pending" ? (
        <>
          <button onClick={() => decide(row.id, "approve")} className="btn-ghost h-7 w-7 p-0 text-ok" title="Approuver">
            <CheckCircle2 size={14} />
          </button>
          <button onClick={() => decide(row.id, "reject")} className="btn-ghost h-7 w-7 p-0 text-bad" title="Rejeter">
            <XCircle size={14} />
          </button>
        </>
      ) : null}
      fields={[
        { key: "type", label: "Type de demande", type: "select", options: ["demande_achat", "conge", "depense", "autre"], required: true, group: 1 },
        { key: "title", label: "Titre", required: true, group: 1 },
        { key: "target_role", label: "À approuver par (rôle)", type: "select", options: ["PDG", "DG", "DAF", "DCM", "RH"], required: true, group: 2 },
        { key: "amount", label: "Montant (XAF, si applicable)", type: "number", group: 2 },
        { key: "description", label: "Description / Justification", type: "textarea", required: true },
      ]}
      defaults={{ type: "depense", title: "", target_role: "DG", amount: 0, description: "" }}
    />
  );
}
